// Carries the score a visitor just received from one of the free tools into
// whatever lead they submit next, so the "New Lead Received" notification tells
// the sales team how the site/profile actually scored before they call.

import type { LeadFieldOut } from "@/lib/leadIngest";

const KEY = "ls_tool_result_context_v1";
/** Older than this and the score is no longer useful context for a call. */
const TTL_MS = 3 * 60 * 60 * 1000;

interface StoredContext {
  savedAt: number;
  fields: LeadFieldOut[];
}

export function setToolResultContext(fields: LeadFieldOut[]): void {
  if (typeof window === "undefined") return;
  try {
    const payload: StoredContext = { savedAt: Date.now(), fields };
    window.sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* storage disabled or full — the notification just loses the extra context */
  }
}

export function clearToolResultContext(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Extra notification fields describing the visitor's most recent tool result. */
export function getToolResultContextFields(): LeadFieldOut[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredContext;
    if (!parsed?.fields?.length) return [];
    if (!parsed.savedAt || Date.now() - parsed.savedAt > TTL_MS) {
      clearToolResultContext();
      return [];
    }
    return parsed.fields;
  } catch {
    return [];
  }
}
